import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { LanguageTextboxComponent } from '@components/language-textbox/language-textbox.component';
import {
    PageTitle,
    SearchChannelGroupComponent,
    SelectedChannel,
} from '@components/search-channel-group/search-channel-group.component';
import { ChannelHistory } from '@models/channel-history';
import { ChannelHistoryService } from '@service/channel-history.service';
import { LanguageService } from '@service/language.service';
import { TrendGraphSettingsService } from '@service/trend-graph-settings.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { TREND_GRAPH_MAX_CHANNELS } from 'ops/common/validators/trend-graph/trend-graph-view-validator';
import { Observable, first, firstValueFrom, skip } from 'rxjs';
import lodash from 'lodash';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { ResponsiveService } from '@service/responsive.service';
import { TranslateService } from '@ngx-translate/core';
import { TrendGraphAccessSettingsService } from '@service/trend-graph-access-settings.service';
import { HttpStatusCode } from '@angular/common/http';
import { AuthService } from '@service/auth.service';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { Channel } from 'ops/common/models/channel';
import { ChannelGroup } from 'ops/common/models/channel-group';

@Component({
    selector: 'app-trend-graph-create',
    templateUrl: './trend-graph-create.component.html',
    styleUrls: ['./trend-graph-create.component.scss'],
    providers: [ChannelHistoryService],
    standalone: false
})
export class TrendGraphCreateComponent implements OnInit, OnChanges, OnDestroy {
    MAX_CHANNELS: number = TREND_GRAPH_MAX_CHANNELS;
    DEFAULT_TITLE: string = 'Custom Trend Graph Name';

    @ViewChild('languageInput') languageInput: LanguageTextboxComponent;
    @ViewChild('searchChannelComponent') searchChannelComponent: SearchChannelGroupComponent;
    @ViewChild('conflictNotification') conflictNotification: PopUpNotificationComponent;
    @Input() view: TrendGraphView;
    @Input() views: TrendGraphView[];
    @Input() viewNameNumber: number;
    @Input() channelLookup: { [fcuChannelId: number]: Channel };
    @Input() channels: Channel[] = [];
    @Input() usersAccess = [];
    @Input() isEditing: boolean = false

    @Output() onCancel: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onSubmit: EventEmitter<TrendGraphView> = new EventEmitter();

    private updateInterval: NodeJS.Timeout = null;
    private MIN_DATE_INDEX = 0;
    private MAX_DATE_INDEX = 1999;

    readonly pageTitle: PageTitle = PageTitle.TrendGraph;

    onChannelValueUpdate: Observable<ChannelHistory>;
    colorOptions: string[] = [];
    showGroups: boolean = false;
    selectedChannels = [];
    channelGroups: ChannelGroup[] = [];
    isChannelLoadingComplete = false;
    languageInputActive = false;
    assignedLanguages: Set<string> = new Set();
    editedView: TrendGraphView;
    showGraphPage: boolean = false;
    graphMinHeight = '30vh';
    titleError: string;
    dateRange = { startIndex: this.MIN_DATE_INDEX, endIndex: this.MAX_DATE_INDEX };
    allowedRegex = { EN: /^[a-zA-Z0-9\s.,!?()-]+$/ };
    disableAddLanguage: boolean = true;
    hasInputError: boolean = false;

    initialEditedView: TrendGraphView;
    initialUsersAccess;

    displaySuggestions: boolean = false;

    constructor(
        private historyService: ChannelHistoryService,
        private viewsService: TrendGraphViewsService,
        public languageService: LanguageService, //Used on the page
        private trendGraphSettings: TrendGraphSettingsService,
        public responsive: ResponsiveService,
        private translateService: TranslateService,
        private trendGraphAccessService: TrendGraphAccessSettingsService,
        private authService: AuthService,
    ) {
        this.colorOptions = this.viewsService.getDefaultColors();
    }

    ngOnInit(): void {
        this.onChannelValueUpdate = this.historyService.onHistoryUpdated();
        this.startRefresing();
        this.calculateGraphHeight();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.view) {
            if (this.view) {
                this.syncToView(this.view);
            } else {
                this.editedView = {
                    name: `${this.DEFAULT_TITLE} ${this.viewNameNumber}`,
                    description: '',
                    channels: [],
                    publicAccess: true,
                };
                //For edit detection
                this.view = JSON.parse(JSON.stringify(this.editedView));
            }

            this.initialEditedView = lodash.cloneDeep(this.editedView);
            this.initialUsersAccess = lodash.cloneDeep(this.usersAccess);
        }
    }

    ngOnDestroy(): void {
        this.stopRefreshing();
        this.historyService.Cleanup();
    }

    @HostListener('window:resize')
    handleWindowResize() {
        if (this.isLargeScreen() && this.showGraphPage) {
            this.showGraphPage = false;
        }
        this.calculateGraphHeight();
    }

    onLoadingCompleted() {
        this.isChannelLoadingComplete = true;
    }

    async submit() {
        const clone = JSON.parse(JSON.stringify(this.editedView));
        if (!this.viewNameNumber) {
            try {
                await this.viewsService.update(clone);
                await this.trendGraphAccessService.update(this.usersAccess);
                this.onSubmit.emit(clone);
            } catch (err) {
                if (err.status == HttpStatusCode.Unauthorized) {
                    this.authService.signOut();
                    this.authService.showSignInOverlay();
                    const loggedInUser = await firstValueFrom(
                        this.authService.currentUser$.pipe(skip(1), first()),
                    );
                    if (loggedInUser.token && loggedInUser.userId) {
                        this.submit();
                    }
                } else {
                    console.error(err);
                }
            }
        } else {
            try {
                const newTrendGrapId = await this.viewsService.save(clone);
                await this.trendGraphAccessService.create(this.usersAccess, newTrendGrapId);
                this.onSubmit.emit(clone);
            } catch (err) {
                if (err.status == HttpStatusCode.Unauthorized) {
                    this.authService.signOut();
                    this.authService.showSignInOverlay();
                    const loggedInUser = await firstValueFrom(
                        this.authService.currentUser$.pipe(skip(1), first()),
                    );
                    if (loggedInUser.token && loggedInUser.userId) {
                        this.submit();
                    }
                } else if (err.status == HttpStatusCode.Conflict) {
                    this.conflictNotification.setVisible(true);
                    console.error(err);
                } else {
                    console.error(err);
                }
            }
        }
    }

    syncToView(view: TrendGraphView) {
        this.editedView = JSON.parse(JSON.stringify(view));
        const fcuIds = view.channels.map((v) => v.fcuChannelId);
        this.historyService.setWatchedChannels(fcuIds);
    }

    toggleSelectedChannels(channelsSelected: SelectedChannel[]) {
        this.stopRefreshing();

        this.editedView.channels = [];
        this.selectedChannels = channelsSelected;
        for (let i = 0; i < channelsSelected.length; i++) {
            this.addChannel(channelsSelected[i]);
        }

        this.historyService.setWatchedChannels(this.editedView.channels.map((v) => v.fcuChannelId));

        this.startRefresing();
    }

    addChannel(channel: SelectedChannel) {
        const selectedColor = this.colorOptions[this.editedView.channels.length];
        this.editedView.channels.push({
            fcuChannelId: channel.fcuChannelId,
            color: selectedColor,
            name: channel.name,
            channelId: channel.channelId,
        });
        this.editedView = { ...this.editedView };
    }

    editDescription(description: string) {
        this.editedView.description = description;
        this.editedView = Object.assign({}, this.editedView);
    }

    startRefresing() {
        if (this.updateInterval !== null) return;

        this.updateInterval = setInterval(() => {
            this.historyService.refresh();
        }, this.trendGraphSettings.updateInterval);
    }

    stopRefreshing() {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
    }

    public hasUnsavedChanges(): boolean {
        return !lodash.isEqual(this.view, this.editedView);
    }

    removeChannel(channel) {
        this.searchChannelComponent.unselectChannel(channel.channelId);
    }

    checkRequiredConditions(): boolean {
        return (
            this.editedView.name.trim().length !== 0 &&
            this.editedView.channels.length > 0 &&
            !this.titleIsRepeated() &&
            !this.hasInputError &&
            this.trendGraphHasChanged()
        );
    }

    isAtDefaultZoom() {
        return (
            this.dateRange.startIndex == this.MIN_DATE_INDEX &&
            this.dateRange.endIndex == this.MAX_DATE_INDEX
        );
    }

    resetZoom() {
        this.dateRange.startIndex = this.MIN_DATE_INDEX;
        this.dateRange.endIndex = this.MAX_DATE_INDEX;
    }

    onShowGraphPage() {
        this.showGraphPage = true;
    }

    isLargeScreen() {
        return this.responsive.isXLarge() || this.responsive.isLarge();
    }

    calculateGraphHeight() {
        if (this.isLargeScreen()) {
            this.graphMinHeight = '30vh';
        } else {
            this.graphMinHeight = '50vh';
        }
    }

    onCloseGraphPreview() {
        this.showGraphPage = false;
    }

    cancel() {
        if (!this.trendGraphHasChanged()) {
            this.onCancel.emit(false);
        } else {
            this.onCancel.emit(true);
        }
    }

    titleIsRepeated() {
        let hasError = false;
        if (this.views) {
            this.views.map((view) => {
                if (view.name === this.editedView.name && view.id !== this.editedView.id) {
                    hasError = true;
                    this.titleError = this.translateService.instant(
                        'trend-graph.create-graph.error.title.repeat',
                    );
                }
            });
        }
        return hasError;
    }

    hasInvalidCharacters(translation) {
        const langugage = translation.language;
        const input = translation.value;
        const allowedPattern = this.allowedRegex[langugage];
        const validated = allowedPattern?.test(input);
        if (allowedPattern && !validated && input !== '') {
            this.hasInputError = true;
            return true;
        } else if (input) {
            this.hasInputError = false;
            return false;
        }
    }

    onInputError(hasError) {
        this.disableAddLanguage = hasError;
    }

    handleTitleChange() {
        if (!this.editedView.name || this.editedView.name.trim().length === 0) {
            this.titleError = this.translateService.instant('trend-graph.create.error.title.empty');
            return;
        }
        this.view.name = this.editedView.name;
    }

    handleAccessChanged(isPublic) {
        this.editedView.publicAccess = isPublic;
    }

    handleUserAccessChanged(newUsersPermissions) {
        if (this.usersAccess.length > 0) {
            this.usersAccess.map((access) => {
                if (access.roleId in newUsersPermissions) {
                    access.accessLevel = newUsersPermissions[access.roleId];
                }
            });
        } else {
            for (let user in newUsersPermissions) {
                this.usersAccess.push({
                    roleId: user,
                    accessLevel: newUsersPermissions[user],
                });
            }
            if (this.initialUsersAccess?.length === 0) {
                this.initialUsersAccess = lodash.cloneDeep(this.usersAccess);
            }
        }
    }

    trendGraphHasChanged() {
        if (!lodash.isEqual(this.initialEditedView, this.editedView)) {
            return true;
        } else {
            if (!lodash.isEqual(this.initialUsersAccess, this.usersAccess)) {
                return true;
            }
        }

        return false;
    }

    getTitleList(): string[] {
        return this.views.map((view) => {
            return view.name;
        });
    }
}
